import { queryOptions, type QueryClient } from "@tanstack/react-query"
import { api } from "./api"
import { listAllData } from "./list-all"
import type { FieldSpec } from "./people"

// Forms data layer. A "form" is a todo carrying `fields` (the form schema);
// per-guest answers live in todo_assignments keyed by `field.id`. The
// backend models forms as the `todos` table — there is no separate forms
// table — so the endpoints here are the same actions the admin SPA's
// TodoBuilder / TodoResponses pages use:
//
//   • GET  /api/v1/todos                                  list (auto-CRUD)
//   • GET  /api/v1/todos/:id                              one row (auto-CRUD)
//   • POST /api/v1/todos/create-todo                      create (dry-run aware)
//   • POST /api/v1/todos/update-todo                      update
//   • POST /api/v1/todos/delete-todo                      delete
//   • POST /api/v1/event/:eventId/todos/:todoId/responses per-guest responses
//   • POST /api/v1/event/:eventId/attendee-fields/setup   fields + groups
//
// "Forms" in the Console surfaces only the form-bearing slice — a form must
// have a non-empty `fields` array. Plain (fieldless) todos are out of scope
// for this view.

// ── field types (mirror quickback/features/todos/lib/form-fields.ts) ─────────

export type FormFieldOption = { value: string; label: string }

export type ShowIfRule = { field: string; equals: string }

export type FormFieldType =
  | "short_text"
  | "long_text"
  | "single_select"
  | "multi_select"
  | "number"
  | "date"
  | "boolean"
  | "rating"
  | "signature"
  | "attachment"
  | "allergies"
  | "secure_text"
  | "section_heading"
  | "paragraph"
  | "markdown"
  | "divider"

export type FormField = {
  id: string
  type: FormFieldType
  label: string
  required?: boolean
  attendeeFieldKey?: string
  showIf?: ShowIfRule
  // short / long text
  placeholder?: string
  maxLength?: number
  inputMode?: "text" | "email" | "phone" | "url"
  // single / multi select
  options?: FormFieldOption[]
  displayAs?: "list" | "dropdown"
  // number
  min?: number
  max?: number
  step?: number
  // date
  mode?: "date" | "datetime" | "time"
  /** One-tap "Today" / "Now" button in the attendee apps. */
  todayShortcut?: boolean
  // rating
  maxRating?: number
  // attachment
  accept?: "any" | "images" | "documents"
  sources?: Array<"file" | "photo" | "camera">
  maxFiles?: number
  maxFileSizeMb?: number
  // allergies (structured allergen list → guest_dietary.allergens)
  allowOther?: boolean
  // secure_text (end-to-end encrypted; the answer is a vault reference)
  documentKind?: SecureDocumentKind
}

export type SecureDocumentKind = "passport" | "national_id" | "visa" | "drivers_license" | "other"

export type FormAudience =
  | "event"
  | "agenda_group"
  | "agenda_item"
  | "attendee_group"
  | "ticket_type"
  | "individual"

export type ResponsePolicy = "editable" | "one_time"
export type FormFlavor = "generic" | "important"
export type ResponseVisibility = "self" | "private"
// Lifecycle: "published" (default) = live for the audience; "draft" = staff-only
// until flipped. Status moves via updateForm({ status }).
export type FormStatus = "draft" | "published"

// A todo row as returned by the auto-CRUD list/get. `fields` is a JSON
// string (or null) — parse with parseFields().
export type FormRow = {
  id: string
  eventId: string
  audience: FormAudience
  agendaItemId: string | null
  agendaGroupId: string | null
  attendeeGroupId: string | null
  ticketTypeId: string | null
  title: string
  body: string | null
  fields: string | null
  responsePolicy: ResponsePolicy | null
  flavor: FormFlavor | null
  responseVisibility: ResponseVisibility | null
  // Missing on rows cached before the column existed → treat as published.
  status?: FormStatus | null
  // Official / system-form marker (e.g. "dietary"). Null = ordinary form.
  systemKey?: string | null
  createdAt?: string | null
  modifiedAt?: string | null
}

type ListEnvelope<T> = { data: T[]; meta?: { total?: number } }

// ── query keys ───────────────────────────────────────────────────────────────
// Match the auto-CRUD resource key shape (["resource", <table>, <filters>]) so
// the vendored realtime layer can patch these caches on live CRUD events.
export const formsKey = (eventId: string) =>
  ["resource", "todos", { eventId, mode: "forms" }] as const
export const formKey = (formId: string) => ["resource", "todos", formId] as const
export const formResponsesKey = (eventId: string, formId: string) =>
  ["form-responses", eventId, formId] as const
export const formSetupKey = (eventId: string) =>
  ["attendee-fields-setup", eventId, "forms"] as const

// ── query options ─────────────────────────────────────────────────────────────

// All todos for the event. The Forms view filters to form-bearing rows
// (non-empty `fields`) client-side via isForm().
export function formsQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: formsKey(eventId),
    queryFn: () =>
      listAllData<FormRow>("/api/v1/todos", { eventId, sort: "createdAt", order: "desc" }),
    select: (r) => r.data,
  })
}

export function formQueryOptions(formId: string) {
  return queryOptions({
    queryKey: formKey(formId),
    // Auto-CRUD GET returns the row directly (not wrapped in { data }).
    queryFn: () => api<FormRow>(`/api/v1/todos/${formId}`),
  })
}

// Audience-picker setup payload (custom attendee fields + membership groups).
export type FormSetupField = { id: string; key: string; label: string; type: string }
export type FormSetupGroup = { id: string; name: string }
export type FormSetupResponse = {
  success?: boolean
  fields?: FormSetupField[]
  groups?: FormSetupGroup[]
}

export function formSetupQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: formSetupKey(eventId),
    queryFn: () =>
      api<FormSetupResponse>(`/api/v1/event/${eventId}/attendee-fields/setup`, {
        method: "POST",
        body: {},
      }),
  })
}

// Audience surfaces for the builder (agenda items / groups).
export function agendaItemsLiteQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: ["agenda-items", eventId, "forms-lite"] as const,
    queryFn: () =>
      listAllData<{ id: string; name: string; groupId: string | null }>(
        "/api/v1/agenda-items",
        { eventId },
      ),
    select: (r) => r.data,
  })
}

export function agendaGroupsLiteQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: ["agenda-groups", eventId, "forms-lite"] as const,
    queryFn: () =>
      listAllData<{ id: string; name: string }>("/api/v1/agenda-groups", { eventId }),
    select: (r) => r.data,
  })
}

export function guestsLiteQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: ["guests", eventId, "forms-lite"] as const,
    queryFn: () =>
      listAllData<{ id: string; firstName: string | null; lastName: string | null; displayName: string | null }>(
        "/api/v1/guests",
        { eventId },
      ),
    select: (r) => r.data,
  })
}

// ── responses viewer ──────────────────────────────────────────────────────────

export type FormResponseRow = {
  guestId: string
  firstName: string | null
  lastName: string | null
  emailAtInvite: string | null
  guestStatus: string | null
  completedAt: string | null
  response: Record<string, unknown> | null
}

export type FormResponsesResult = {
  success?: boolean
  todo?: {
    id: string
    title: string
    audience: FormAudience
    flavor: FormFlavor
    responseVisibility: ResponseVisibility | null
    fields: FieldSpec[] | null
  }
  rows?: FormResponseRow[]
}

export function formResponsesQueryOptions(eventId: string, formId: string) {
  return queryOptions({
    queryKey: formResponsesKey(eventId, formId),
    queryFn: () =>
      api<FormResponsesResult>(
        `/api/v1/event/${eventId}/todos/${formId}/responses`,
        { method: "POST", body: { todoId: formId } },
      ),
  })
}

// ── templates ─────────────────────────────────────────────────────────────────
// Curated starter forms from the static server registry
// (POST /api/v1/templates/list-todo-templates). Mirrors the backend
// `TodoTemplate` shape (quickback/features/templates/lib/types.ts). A
// template owns nothing in the DB — picking one hands a draft to the builder
// which the admin tweaks + saves via the normal create flow.
export type TodoTemplate = {
  id: string
  title: string
  description: string
  // SF-Symbol-compatible name; the picker maps it to a lucide icon.
  icon: string
  deprecated?: boolean
  // Official / system-form marker (e.g. "dietary"). Plain templates omit it.
  systemKey?: string
  defaults: {
    title: string
    body?: string
    flavor: FormFlavor
    audience: FormAudience
    responsePolicy: ResponsePolicy
    responseVisibility: ResponseVisibility
    fields: FormField[]
  }
}

export const todoTemplatesKey = ["todo-templates"] as const

export function todoTemplatesQueryOptions() {
  return queryOptions({
    queryKey: todoTemplatesKey,
    queryFn: () =>
      api<{ success: boolean; templates: TodoTemplate[] }>(
        "/api/v1/templates/list-todo-templates",
        { method: "POST", body: {} },
      ),
    select: (r) => r.templates ?? [],
    // Static catalog — cache aggressively; it changes only on deploy.
    staleTime: 30 * 60 * 1000,
  })
}

// ── mutations ─────────────────────────────────────────────────────────────────

export type CreateFormInput = {
  eventId: string
  title: string
  body?: string
  audience: FormAudience
  agendaItemId?: string
  agendaGroupId?: string
  attendeeGroupId?: string
  ticketTypeId?: string
  guestIds?: string[]
  fields: FormField[]
  responsePolicy?: ResponsePolicy
  flavor?: FormFlavor
  responseVisibility?: ResponseVisibility
  status?: FormStatus
  // Official / system-form marker, carried from an official template.
  systemKey?: string
  dryRun?: boolean
}

export type CreateFormResult = {
  success: boolean
  dryRun?: boolean
  wouldCreate?: unknown
  todo?: FormRow
  assignmentCount?: number
}

export async function createForm(input: CreateFormInput): Promise<CreateFormResult> {
  return api<CreateFormResult>("/api/v1/todos/create-todo", {
    method: "POST",
    body: {
      eventId: input.eventId,
      title: input.title,
      body: input.body || undefined,
      audience: input.audience,
      agendaItemId: input.audience === "agenda_item" ? input.agendaItemId : undefined,
      agendaGroupId: input.audience === "agenda_group" ? input.agendaGroupId : undefined,
      attendeeGroupId:
        input.audience === "attendee_group" ? input.attendeeGroupId : undefined,
      ticketTypeId: input.audience === "ticket_type" ? input.ticketTypeId : undefined,
      guestIds: input.audience === "individual" ? input.guestIds : undefined,
      fields: input.fields.length > 0 ? input.fields : undefined,
      responsePolicy:
        input.fields.length > 0 && input.responsePolicy === "one_time"
          ? "one_time"
          : undefined,
      flavor: input.flavor === "important" ? "important" : undefined,
      responseVisibility:
        input.fields.length > 0 && input.responseVisibility === "private"
          ? "private"
          : undefined,
      status: input.status === "draft" ? "draft" : undefined,
      systemKey: input.systemKey || undefined,
      dryRun: input.dryRun,
    },
  })
}

export type UpdateFormInput = {
  formId: string
  title: string
  body?: string | null
  fields: FormField[]
  responsePolicy?: ResponsePolicy
  flavor?: FormFlavor
  responseVisibility?: ResponseVisibility
  status?: FormStatus
}

export async function updateForm(
  input: UpdateFormInput,
): Promise<{ success: boolean; todo?: FormRow }> {
  return api<{ success: boolean; todo?: FormRow }>("/api/v1/todos/update-todo", {
    method: "POST",
    body: {
      todoId: input.formId,
      title: input.title.trim(),
      body: input.body ? input.body : null,
      fields: input.fields.length > 0 ? input.fields : null,
      responsePolicy: input.fields.length > 0 ? input.responsePolicy ?? "editable" : null,
      flavor: input.flavor ?? "generic",
      responseVisibility:
        input.fields.length > 0 ? input.responseVisibility ?? "self" : null,
      ...(input.status ? { status: input.status } : {}),
    },
  })
}

export async function deleteForm(formId: string): Promise<{ success: boolean }> {
  return api<{ success: boolean }>("/api/v1/todos/delete-todo", {
    method: "POST",
    body: { todoId: formId },
  })
}

// Mint a public no-login share link to this form's responses (rendered by
// apps/share at /form-responses/:token). Defaults to expiring at the event's end.
export async function createFormShareLink(
  formId: string,
): Promise<{ success: boolean; url: string; token: string; expiresAt: string | null }> {
  return api<{ success: boolean; url: string; token: string; expiresAt: string | null }>(
    "/api/v1/agenda-items/create-form-share-link",
    { method: "POST", body: { formId } },
  )
}

export type FormShareLink = {
  url: string
  token: string
  live: boolean
  expiresAt: string | null
  revokedAt: string | null
  createdAt: string | null
}

// List the public share links minted for this form's responses (show/copy/revoke
// in the console). Backed by the same activity_share_links table + FGA grant.
export function formShareLinksQueryOptions(formId: string) {
  return {
    queryKey: ["form-share-links", formId] as const,
    queryFn: () =>
      api<{ success: true; links: FormShareLink[] }>(
        "/api/v1/agenda-items/list-form-share-links",
        { method: "POST", body: { formId } },
      ),
  }
}

export function revokeFormShareLink(token: string) {
  return api<{ success: true; revokedCount: number; revokedTokens: string[] }>(
    "/api/v1/agenda-items/revoke-form-share-link",
    { method: "POST", body: { token } },
  )
}

// ── helpers ───────────────────────────────────────────────────────────────────

// A form is a todo with at least one field. Plain todos are excluded from
// the Forms view.
export function isForm(row: FormRow): boolean {
  return !!row.fields && row.fields !== "null" && row.fields !== "[]"
}

export function parseFields(raw: string | null): FormField[] {
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as FormField[]) : []
  } catch {
    return []
  }
}

export function questionCount(row: FormRow): number {
  return parseFields(row.fields).filter(isQuestionField).length
}

const DISPLAY_TYPES = new Set<FormFieldType>([
  "section_heading",
  "paragraph",
  "markdown",
  "divider",
])

// A "question" is an answerable field — display-only fields don't count.
export function isQuestionField(f: FormField): boolean {
  return !DISPLAY_TYPES.has(f.type)
}

export function isDisplayField(f: FormField): boolean {
  return DISPLAY_TYPES.has(f.type)
}

export function audienceLabel(
  row: Pick<FormRow, "audience" | "attendeeGroupId" | "ticketTypeId">,
  groupNameById?: Map<string, string>,
  ticketNameById?: Map<string, string>,
): string {
  switch (row.audience) {
    case "event":
      return "All attendees"
    case "agenda_group":
      return "By agenda group"
    case "agenda_item":
      return "By agenda item"
    case "attendee_group": {
      const name = row.attendeeGroupId
        ? groupNameById?.get(row.attendeeGroupId)
        : undefined
      return name ? `Membership group · ${name}` : "Membership group"
    }
    case "ticket_type": {
      const name = row.ticketTypeId
        ? ticketNameById?.get(row.ticketTypeId)
        : undefined
      return name ? `Ticket type · ${name}` : "Ticket type"
    }
    case "individual":
      return "Specific guests"
  }
}

// Splice an updated form row into the list cache (the realtime-patched key).
// Returns true when the row was found + patched.
export function patchFormInCache(
  qc: QueryClient,
  eventId: string,
  row: { id: string } & Partial<FormRow>,
): boolean {
  let patched = false
  qc.setQueryData<ListEnvelope<FormRow>>(formsKey(eventId), (old) => {
    if (!old) return old
    const next = old.data.map((r) => {
      if (r.id !== row.id) return r
      patched = true
      return { ...r, ...row }
    })
    return patched ? { ...old, data: next } : old
  })
  return patched
}
